import { buttonTextVariants, buttonVariants } from "@/components/ui/button";
import { NativeOnlyAnimatedView } from "@/components/ui/native-only-animated-view";
import { TextClassContext } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import * as AlertDialogPrimitive from "@rn-primitives/alert-dialog";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { Modal, Platform, View, type ViewProps } from "react-native";
import { FadeIn, FadeOut, ReduceMotion } from "react-native-reanimated";
import { FullWindowOverlay as RNFullWindowOverlay } from "react-native-screens";

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

function AlertDialogOverlay({
    className,
    children,
    ...props
}: Omit<
    React.ComponentProps<typeof AlertDialogPrimitive.Overlay>,
    "asChild"
> & {
    children?: React.ReactNode;
}) {
    const { onOpenChange } = AlertDialogPrimitive.useRootContext();
    const overlay = (
        <AlertDialogPrimitive.Overlay
            className={cn(
                "absolute bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-black/50 p-2",
                Platform.select({
                    web: "animate-in fade-in-0 fixed",
                }),
                className
            )}
            {...props}
            asChild={Platform.OS !== "web"}
        >
            <NativeOnlyAnimatedView
                entering={FadeIn.duration(200)
                    .delay(50)
                    .reduceMotion(ReduceMotion.System)}
                exiting={FadeOut.duration(150).reduceMotion(
                    ReduceMotion.System
                )}
                as="Pressable"
            >
                <>{children}</>
            </NativeOnlyAnimatedView>
        </AlertDialogPrimitive.Overlay>
    );

    if (Platform.OS === "ios") {
        return <RNFullWindowOverlay>{overlay}</RNFullWindowOverlay>;
    }

    // SheetModal is its own layer (native window on Android, body portal on
    // web). A view portaled into the app root stays under it, so the alert
    // needs its own Modal to receive touches.
    return (
        <Modal
            transparent
            animationType="none"
            onRequestClose={() => onOpenChange(false)}
            {...(Platform.OS === "android"
                ? {
                      statusBarTranslucent: true,
                      navigationBarTranslucent: true,
                  }
                : null)}
        >
            <View className="flex-1">{overlay}</View>
        </Modal>
    );
}

function AlertDialogContent({
    className,
    portalHost,
    ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content> & {
    portalHost?: string;
}) {
    return (
        <AlertDialogPortal hostName={portalHost}>
            <AlertDialogOverlay>
                <AlertDialogPrimitive.Content
                    className={cn(
                        "bg-background border-border z-50 flex w-full max-w-[calc(100%-2rem)] flex-col gap-4 rounded-lg border p-6 shadow-lg shadow-black/5 sm:max-w-lg",
                        Platform.select({
                            web: "animate-in fade-in-0 zoom-in-95 duration-200",
                        }),
                        className
                    )}
                    {...props}
                />
            </AlertDialogOverlay>
        </AlertDialogPortal>
    );
}

function AlertDialogHeader({ className, ...props }: ViewProps) {
    return (
        <TextClassContext.Provider value="text-center sm:text-left">
            <View className={cn("flex flex-col gap-2", className)} {...props} />
        </TextClassContext.Provider>
    );
}

function AlertDialogFooter({ className, ...props }: ViewProps) {
    return (
        <View
            className={cn(
                "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
                className
            )}
            {...props}
        />
    );
}

function AlertDialogTitle({
    className,
    ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
    return (
        <AlertDialogPrimitive.Title
            className={cn("text-foreground text-lg font-semibold", className)}
            {...props}
        />
    );
}

function AlertDialogDescription({
    className,
    ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
    return (
        <AlertDialogPrimitive.Description
            className={cn("text-muted-foreground text-sm", className)}
            {...props}
        />
    );
}

function AlertDialogAction({
    className,
    variant = "default",
    ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> &
    Pick<VariantProps<typeof buttonVariants>, "variant">) {
    return (
        <TextClassContext.Provider value={buttonTextVariants({ variant })}>
            <AlertDialogPrimitive.Action
                className={cn(buttonVariants({ variant }), className)}
                {...props}
            />
        </TextClassContext.Provider>
    );
}

function AlertDialogCancel({
    className,
    ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
    return (
        <TextClassContext.Provider
            value={buttonTextVariants({ className, variant: "outline" })}
        >
            <AlertDialogPrimitive.Cancel
                className={cn(
                    buttonVariants({ variant: "outline" }),
                    className
                )}
                {...props}
            />
        </TextClassContext.Provider>
    );
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger
};

